import React, { useCallback, useState } from 'react';
import { Alert, Modal } from 'antd';
import { Button, message } from 'components';
import { uniqBy } from 'lodash';
import { PopulatedUser } from 'api/UserController/getProfile';
import {
  assignSupraLockbox,
  PermissionsType,
} from 'api/SupraLockboxController';
import { featureEnabled } from 'utils';
import assignSupraLockboxV2 from 'api/SupraLockboxController/assignSupraLockboxV2';
import getSupraUsersByLocation from 'api/UserController/getSupraUsersByLocation';
import type {
  SupraLockboxJsonType,
  IGetSupraUsersByLocationQuery,
} from '@/app-package';
import LockboxSearch from './LockboxSearch';
import { LockboxPermissionType, ShackleSetting } from './types';

type AddLockboxModalPropsType = {
  fetchLockboxData: () => Promise<void>;
  mlsStaffOrgId?: string;
  user?: PopulatedUser;
  isLimitedWithAssignLockboxDisabled: boolean;
  canAssignLockboxesToUser: boolean;
};

const AddLockboxModal = ({
  fetchLockboxData,
  mlsStaffOrgId,
  user,
  isLimitedWithAssignLockboxDisabled,
  canAssignLockboxesToUser,
}: AddLockboxModalPropsType) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [unassignedLockboxes, setUnassignedLockboxes] = useState<
    SupraLockboxJsonType[]
  >([]);
  const [values, setValues] = useState<string[]>([]);
  const [permission, setPermission] = useState<LockboxPermissionType>();
  const [permissions, setPermissions] = useState<PermissionsType[]>([]);
  const [shackleSetting, setShackleSetting] = useState<ShackleSetting>(
    ShackleSetting.None,
  );
  const [shackleUserIds, setShackleUserIds] = useState<string[] | undefined>();
  const [searchOptions, setSearchOptions] = useState<SupraLockboxJsonType[]>(
    [],
  );
  const [loading, setLoading] = useState(false);

  const getUserIds = async () => {
    if (!user) {
      throw new Error('User could not be determined.');
    }

    const SEARCH_QUERY: IGetSupraUsersByLocationQuery = {
      orgId: user.org.id,
      location: user.location,
      address: user.address,
      eligibleGranteesOnly: true,
    };

    const result: string[] = (await getSupraUsersByLocation(SEARCH_QUERY))
      .filter(entry => entry.id !== user?.id)
      .map(u => u.id);

    return result;
  };

  const onValueChange = useCallback(
    lockboxValues => {
      if (lockboxValues.length < 1) {
        setUnassignedLockboxes([]);
        return;
      }

      const lockboxes: any = searchOptions
        .map(lockbox =>
          lockboxValues.includes(lockbox.serialNumber) ? lockbox : null,
        )
        .filter(Boolean);

      const filteredLockboxes: SupraLockboxJsonType[] = lockboxes.filter(
        (lockbox: any) => {
          if (!lockbox) return;

          if (unassignedLockboxes.length === 0) return lockbox;

          return unassignedLockboxes.find(box => box._id !== lockbox._id);
        },
      );

      setUnassignedLockboxes(
        uniqBy([...unassignedLockboxes, ...filteredLockboxes], '_id'),
      );
    },
    [searchOptions],
  );

  const onPermissionChange = (
    lockboxId: string,
    permissionType: LockboxPermissionType,
  ) => {
    if (featureEnabled('combinedLockboxAssignShacklePermissionsFeature')) {
      setPermission(permissionType);
    } else {
      setPermissions([
        ...permissions.filter(({ lockboxId: lid }) => lid !== lockboxId),
        { lockboxId, permissionType },
      ]);
    }
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!user) return;
    try {
      setLoading(true);

      let boxPermissions: PermissionsType[] = [];
      if (featureEnabled('combinedLockboxAssignShacklePermissionsFeature')) {
        if (permission === undefined) {
          message.error('No permission type provided.');
          return;
        }
        boxPermissions = unassignedLockboxes.map(ul => ({ lockboxId: ul._id, permissionType: permission }));
      } else {
        boxPermissions = permissions;
      }

      const userIds: string[] | undefined = [user.id];
      switch (shackleSetting) {
        case ShackleSetting.None:
          // No changes to make
          break;

        case ShackleSetting.Everyone:
          userIds.push(...(await getUserIds()));
          break;

        case ShackleSetting.Individual:
          if (!shackleUserIds || shackleUserIds?.length === 0) {
            // The UI doesn't allow this to happen, but check the data just to be safe
            message.error(
              'No user specified for shackle permissions. No permissions created.',
            );
            return;
          }
          userIds.push(...shackleUserIds);
          break;

        default:
          message.error('Invalid shackle settings. Please try again.');
          return;
      }

      if (featureEnabled('combinedLockboxAssignShacklePermissionsFeature')) {
        await assignSupraLockboxV2({
          permissions: boxPermissions.map(p => ({
            ...p,
            subjectId: user.id,
            // @ts-ignore implicit any
            branchIndex: null,
            ownerOrAssigneeType: 'user',
          })),
          granteeUserIds: userIds,
          officePermissionsGenerated:
            shackleSetting === ShackleSetting.Everyone,
        });
      } else {
        await assignSupraLockbox({
          userId: user.id,
          permissions: boxPermissions,
        });
      }

      message.success('Lockbox added');
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
      fetchLockboxData();
      setIsModalOpen(false);
      setUnassignedLockboxes([]);
      setPermissions([]);
      setSearchOptions([]);
      setValues([]);
      setShackleSetting(ShackleSetting.None);
      setShackleUserIds(undefined);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const assignLockboxDisabled = () => {
    if (unassignedLockboxes.length < 1) return true;

    if (featureEnabled('combinedLockboxAssignShacklePermissionsFeature')) {
      if (
        permission === undefined ||
        (shackleSetting === ShackleSetting.Individual &&
          (!shackleUserIds || shackleUserIds.length === 0))
      )
        return true;
    } else if (unassignedLockboxes.length !== permissions.length) return true;

    return false;
  };

  if (isLimitedWithAssignLockboxDisabled) return null;

  return (
    <>
      <Button
        type="primary"
        style={{ marginLeft: '10px' }}
        onClick={showModal}
        disabled={!canAssignLockboxesToUser}
      >
        Add Lockbox
      </Button>
      <Modal
        title="Add Lockboxes"
        open={isModalOpen}
        onCancel={handleCancel}
        width={'711px'}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            type="primary"
            key="assignLockbox"
            onClick={handleSubmit}
            disabled={assignLockboxDisabled()}
            loading={loading}
          >
            Assign Lockboxes
          </Button>,
        ]}
      >
        <>
          <Alert
            type="info"
            showIcon
            message="Enter the serial number of the existing lockbox to automatically populate its serial number."
          />
          <LockboxSearch
            values={values}
            setValues={setValues}
            mlsStaffOrgId={mlsStaffOrgId}
            user={user}
            onValueChange={onValueChange}
            options={searchOptions}
            setOptions={setSearchOptions}
            unassignedLockboxes={unassignedLockboxes}
            permission={permission}
            shackleSetting={shackleSetting}
            shackleUserIds={shackleUserIds}
            onPermissionChange={onPermissionChange}
            onShackleSettingChange={setShackleSetting}
            onShackleUserIdsChange={setShackleUserIds}
          />
        </>
      </Modal>
    </>
  );
};

export default AddLockboxModal;
